<template>
  <div class="user-manage">
    <div class="card-header">
      <h2 class="title">用户管理</h2>
      <div class="search-wrapper">
        <el-input
          v-model="searchQuery"
          placeholder="搜索用户名/手机号/邀请码"
          class="search-input"
          @input="handleSearch"
        >
          <template #prefix>
            <el-icon><Search /></el-icon>
          </template>
        </el-input>
      </div>
    </div>

    <el-table
      :data="filteredUsers"
      style="width: 100%"
      v-loading="loading"
      border
    >
      <el-table-column prop="id" label="ID" />
      <el-table-column prop="name" label="用户名" />
      <el-table-column prop="phone" label="手机号" />
      <!-- <el-table-column prop="email" label="邮箱" /> -->
      <el-table-column prop="invite_id" label="邀请码" />
      <!-- <el-table-column prop="poor" label="贫困生" /> -->
      <el-table-column label="操作">
        <template #default="scope">
          <div class="action-buttons">
            <el-button
              type="primary"
              link
              @click="viewUserOrders(scope.row)"
              class="action-button"
            >
              <el-icon><Document /></el-icon>
              查看订单
            </el-button>
            <el-button
              type="danger"
              link
              @click="handleDelete(scope.row)"
              class="action-button"
            >
              <el-icon><Delete /></el-icon>
              删除
            </el-button>
          </div>
        </template>
      </el-table-column>
    </el-table>

    <!-- 订单弹窗 -->
    <el-dialog
      v-model="orderDialogVisible"
      :title="'用户 ' + selectedUserName + ' 的订单'"
      width="80%"
      class="order-dialog"
    >
      <el-table
        :data="userOrders"
        style="width: 100%"
        v-loading="orderLoading"
        border
        :header-cell-style="{
          background: '#f5f7fa',
          color: '#606266',
          fontWeight: 600,
        }"
      >
        <el-table-column prop="id" label="订单号" width="80" />
        <el-table-column label="打印选项" min-width="200">
          <template #default="scope">
            <div class="order-details">
              <div class="detail-item">
                <span class="label">颜色：</span>
                <span>{{ scope.row.color ? "彩色" : "黑白" }}</span>
              </div>
              <div class="detail-item">
                <span class="label">页面：</span>
                <span>{{ scope.row.page_text }}</span>
              </div>
              <div class="detail-item">
                <span class="label">尺寸：</span>
                <span>{{ scope.row.size }}</span>
              </div>
              <div class="detail-item">
                <span class="label">材质：</span>
                <span>{{ scope.row.material }}</span>
              </div>
              <div class="detail-item">
                <span class="label">装订：</span>
                <span>{{ scope.row.binding_text }}</span>
              </div>
              <div class="detail-item">
                <span class="label">份数：</span>
                <span>{{ scope.row.copies }}</span>
              </div>
            </div>
          </template>
        </el-table-column>
        <el-table-column label="配送信息" min-width="200">
          <template #default="scope">
            <div class="order-details">
              <div class="detail-item">
                <span class="label">电话：</span>
                <span>{{ scope.row.phone }}</span>
              </div>
              <div class="detail-item">
                <span class="label">地址：</span>
                <span>{{ scope.row.address }}</span>
              </div>
              <div class="detail-item">
                <span class="label">配送时间：</span>
                <span>{{ scope.row.time }}</span>
              </div>
            </div>
          </template>
        </el-table-column>
        <el-table-column prop="price" label="金额" width="100">
          <template #default="scope">
            <span class="price">¥{{ scope.row.price }}</span>
          </template>
        </el-table-column>
        <el-table-column prop="state" label="状态" width="100">
          <template #default="scope">
            <el-tag
              :type="getOrderStateType(scope.row.state)"
              class="state-tag"
              effect="light"
            >
              {{ getOrderStateText(scope.row.state) }}
            </el-tag>
          </template>
        </el-table-column>
        <el-table-column prop="create_time" label="创建时间" width="160" />
      </el-table>
    </el-dialog>
  </div>
</template>

<script>
import { ref, computed } from "vue";
import { ElMessage, ElMessageBox } from "element-plus";
import { Search, Document, Delete } from "@element-plus/icons-vue";
import { userApi, orderApi } from "../api/order";

export default {
  name: "UserManage",
  components: {
    Search,
    Document,
    Delete,
  },
  setup() {
    const users = ref([]);
    const loading = ref(false);
    const searchQuery = ref("");
    const orderDialogVisible = ref(false);
    const orderLoading = ref(false);
    const userOrders = ref([]);
    const selectedUserName = ref("");

    // 过滤用户列表
    const filteredUsers = computed(() => {
      if (!searchQuery.value) return users.value;
      const query = searchQuery.value.toLowerCase();
      return users.value.filter(
        (user) =>
          user.name.toLowerCase().includes(query) ||
          user.phone.includes(query) ||
          user.email.toLowerCase().includes(query) ||
          user.invite_id.toLowerCase().includes(query)
      );
    });

    // 获取用户列表
    const fetchUsers = async () => {
      loading.value = true;
      try {
        const response = await userApi.getUsers();
        users.value = response.data;
        console.log(users.value);
      } catch (error) {
        console.error("获取用户列表失败:", error);
        ElMessage.error("获取用户列表失败");
      } finally {
        loading.value = false;
      }
    };

    // 获取订单状态类型
    const getOrderStateType = (state) => {
      const types = {
        unpaid: "warning",
        printing: "primary",
        delivering: "info",
        completed: "success",
        cancelled: "danger",
      };
      return types[state] || "info";
    };

    // 获取订单状态文本
    const getOrderStateText = (state) => {
      const texts = {
        unpaid: "待支付",
        printing: "打印中",
        delivering: "配送中",
        completed: "已完成",
        cancelled: "已取消",
      };
      return texts[state] || state;
    };

    // 删除用户
    const handleDelete = (user) => {
      ElMessageBox.confirm(
        `确定要删除用户 ${user.name} 吗？此操作将同时删除该用户的所有订单和优惠券记录。`,
        "警告",
        {
          confirmButtonText: "确定",
          cancelButtonText: "取消",
          type: "warning",
        }
      )
        .then(async () => {
          try {
            await userApi.deleteUser(user.id);
            ElMessage.success("删除成功");
            fetchUsers(); // 重新加载用户列表
          } catch (error) {
            console.error("删除用户失败:", error);
            ElMessage.error("删除用户失败");
          }
        })
        .catch(() => {
          ElMessage.info("已取消删除");
        });
    };

    // 查看用户订单
    const viewUserOrders = async (user) => {
      orderLoading.value = true;
      selectedUserName.value = user.name;
      orderDialogVisible.value = true;

      try {
        const response = await orderApi.getOrders(user.id);
        userOrders.value = response.data;
      } catch (error) {
        console.error("获取用户订单失败:", error);
        ElMessage.error("获取用户订单失败");
      } finally {
        orderLoading.value = false;
      }
    };

    // 初始化
    fetchUsers();

    const tableRowClassName = ({ rowIndex }) => {
      return rowIndex % 2 === 0 ? "even-row" : "odd-row";
    };

    return {
      users,
      loading,
      searchQuery,
      filteredUsers,
      handleDelete,
      viewUserOrders,
      handleSearch: () => {}, // 搜索功能通过计算属性自动实现
      orderDialogVisible,
      orderLoading,
      userOrders,
      selectedUserName,
      getOrderStateType,
      getOrderStateText,
      tableRowClassName,
    };
  },
};
</script>

<style scoped>
.user-manage {
  padding: 20px;
  min-height: calc(100vh - 40px);
}

.box-card {
  border-radius: 8px;
  transition: all 0.3s;
}

.card-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 0 10px;
  margin-bottom: 20px;
}

.title {
  margin: 0;
  margin-left: 33vw;
  font-size: 24px;
  color: #303133;
}

.search-wrapper {
  flex: 0 0 300px;
}

.search-input :deep(.el-input__wrapper) {
  box-shadow: 0 0 0 1px #dcdfe6 inset;
  transition: all 0.3s;
}

.search-input :deep(.el-input__wrapper:hover) {
  box-shadow: 0 0 0 1px #000000 inset;
}

.action-button {
  padding: 4px 8px;
  transition: all 0.3s;
  color: #000000;
}

.action-button:hover {
  transform: translateY(-1px);
}

.action-button .el-icon {
  margin-right: 4px;
  vertical-align: middle;
}

.action-buttons {
  display: flex;
  align-items: center;
  gap: 8px;
}

.even-row {
  background-color: #ffffff;
}

.odd-row {
  background-color: #fafafa;
}

.order-dialog :deep(.el-dialog__header) {
  margin: 0;
  padding: 20px;
  background-color: #f5f7fa;
  border-bottom: 1px solid #e4e7ed;
  border-radius: 8px 8px 0 0;
}

.order-details {
  padding: 4px 0;
}

.detail-item {
  line-height: 1.8;
  display: flex;
  align-items: center;
}

.label {
  color: #606266;
  font-weight: 500;
  margin-right: 8px;
  flex: 0 0 70px;
}

.price {
  color: #f56c6c;
  font-weight: 600;
}

.state-tag {
  padding: 4px 8px;
  border-radius: 4px;
}

:deep(.el-table) {
  border: 2px solid #000000;
}

:deep(.el-table--border) {
  border: 2px solid #000000;
}

:deep(.el-table--border::after),
:deep(.el-table--group::after),
:deep(.el-table::before) {
  background-color: #000000;
}

:deep(.el-table td),
:deep(.el-table th.is-leaf) {
  border-bottom: 1px solid #000000;
}

:deep(.el-table--border td),
:deep(.el-table--border th) {
  border-right: 1px solid #000000;
}

:deep(.el-table__fixed-right::before),
:deep(.el-table__fixed::before) {
  background-color: #000000;
}

:deep(.el-table--border th.gutter:last-of-type) {
  border-bottom: 1px solid #000000;
  border-right: 1px solid #000000;
}
</style> 